import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Check, Loader, AlertTriangle, ArrowRight, ChevronDown } from 'lucide-react';
import Gleap from 'gleap';
import { useQuery } from 'convex/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { NestedSquaresLoader } from '@/components/ui/nested-squares-loader';
import { useDevice } from '../contexts/DeviceContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchAppsMetadata, AppMetadata } from '@/lib/appStoreApi';
import { api } from '../api';

// Safari bundle ID to exclude
const SAFARI_BUNDLE_ID = 'com.apple.mobilesafari';

// Check if an app is an Apple system app
const isAppleApp = (bundleId: string): boolean => {
  return bundleId.startsWith('com.apple.');
};

const AppSelection = () => {
  const navigate = useNavigate();
  const { selectedDevice, supervise, fetchInstalledApps } = useDevice();
  const { selectedChild, code } = useAuth();

  const [installedApps, setInstalledApps] = useState<string[]>([]);
  const [appMetadata, setAppMetadata] = useState<Map<string, AppMetadata>>(new Map());
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [metadataProgress, setMetadataProgress] = useState({ loaded: 0, total: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Fetch pre-allowed apps based on device's level/category configuration
  const preAllowedApps = useQuery(
    api.apps.list.allowedByDeviceStatic,
    code ? { deviceCode: code } : 'skip'
  );

  // Fetch installed apps on mount
  useEffect(() => {
    const loadApps = async () => {
      if (!selectedDevice?.udid) {
        setError('No device selected');
        setIsLoading(false);
        return;
      }

      try {
        const apps = await fetchInstalledApps(selectedDevice.udid);
        setInstalledApps(apps);
        // Select all apps by default
        setSelectedApps(new Set(apps));
        setIsLoading(false);

        // Fetch metadata for all apps
        if (apps.length > 0) {
          setIsLoadingMetadata(true);
          setMetadataProgress({ loaded: 0, total: apps.length });
          
          const metadata = await fetchAppsMetadata(apps, (loaded, total) => {
            setMetadataProgress({ loaded, total });
          });
          
          setAppMetadata(metadata);
          setIsLoadingMetadata(false);
        }
      } catch (err) {
        console.error('Failed to fetch apps:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch installed apps');
        setIsLoading(false);
      }
    };

    loadApps();
  }, [selectedDevice?.udid, fetchInstalledApps]);

  // Get app display info (name, icon) with fallback to bundle ID
  const getAppInfo = (bundleId: string) => {
    const metadata = appMetadata.get(bundleId);
    return {
      name: metadata?.name || bundleId,
      icon: metadata?.icon || null,
      developer: metadata?.developer || null,
    };
  };

  // Filter apps: must have icon, exclude Safari
  const appsWithIcons = useMemo(() => {
    return installedApps.filter(bundleId => {
      // Exclude Safari
      if (bundleId === SAFARI_BUNDLE_ID) return false;
      // Only include apps with icons
      const metadata = appMetadata.get(bundleId);
      return metadata?.icon != null;
    });
  }, [installedApps, appMetadata]);

  // Filter apps based on search query (search by name and bundle ID)
  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return appsWithIcons;
    const query = searchQuery.toLowerCase();
    return appsWithIcons.filter(bundleId => {
      const info = getAppInfo(bundleId);
      return (
        bundleId.toLowerCase().includes(query) ||
        info.name.toLowerCase().includes(query) ||
        (info.developer && info.developer.toLowerCase().includes(query))
      );
    });
  }, [appsWithIcons, searchQuery, appMetadata]);

  // Separate and sort apps: non-Apple first, then Apple apps
  const { thirdPartyApps, appleApps } = useMemo(() => {
    const thirdParty: string[] = [];
    const apple: string[] = [];
    
    filteredApps.forEach(bundleId => {
      if (isAppleApp(bundleId)) {
        apple.push(bundleId);
      } else {
        thirdParty.push(bundleId);
      }
    });

    // Sort each group alphabetically
    const sortByName = (a: string, b: string) => {
      const nameA = getAppInfo(a).name.toLowerCase();
      const nameB = getAppInfo(b).name.toLowerCase();
      return nameA.localeCompare(nameB);
    };

    return {
      thirdPartyApps: thirdParty.sort(sortByName),
      appleApps: apple.sort(sortByName),
    };
  }, [filteredApps, appMetadata]);

  const [appleAppsOpen, setAppleAppsOpen] = useState(false);
  const appleAppsSectionRef = useRef<HTMLDivElement>(null);

  const handleToggleApp = (bundleId: string) => {
    setSelectedApps(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedApps(new Set(installedApps));
  };

  const handleClearAll = () => {
    setSelectedApps(new Set());
  };

  const handleContinue = async () => {
    if (!selectedDevice?.udid) {
      setError('No device selected');
      return;
    }

    setError(null);
    setIsProcessing(true);
    setLoadingProgress(0);

    // Simulate progress during loading
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 500);

    try {
      // Only send apps that are visible to the user (have icons, not Safari)
      const visibleAllowed = appsWithIcons.filter(id => selectedApps.has(id));
      const visibleBlocked = appsWithIcons.filter(id => !selectedApps.has(id));
      
      // Deduplicate: remove apps already allowed by level/category from the whitelist
      const preAllowedBundleIds = new Set(
        (preAllowedApps || []).map(app => app.bundleId)
      );
      const deduplicatedAllow = visibleAllowed.filter(
        bundleId => !preAllowedBundleIds.has(bundleId)
      );
      
      console.log('App whitelist deduplication:', {
        totalAllowed: visibleAllowed.length,
        preAllowedByLevel: preAllowedBundleIds.size,
        deduplicatedCount: deduplicatedAllow.length,
      });
      
      await supervise(selectedDevice.udid, { allow: deduplicatedAllow, block: visibleBlocked }, () => {
        clearInterval(progressInterval);
        setLoadingProgress(100);
        
        setTimeout(() => {
          navigate('/confirmation');
        }, 1000);
      });
    } catch (err) {
      console.error('Supervision failed:', err);
      clearInterval(progressInterval);
      
      const errorMessage = err instanceof Error ? err.message : String(err);
      
      if (errorMessage.includes('MBErrorDomain') && errorMessage.includes('Encrypted Backup MDM setting present')) {
        setError('This device is already filtered');
        // Set custom data for support context before triggering bot
        Gleap.attachCustomData({
          install_step: "mdm_profile_install",
          error_code: "DEVICE_ALREADY_FILTERED",
          device_serial: selectedDevice?.serial || "unknown",
          error_message: errorMessage,
        });
        Gleap.startBot('6980f8e0d2ef8797d6c8f0f9');
      } else if (errorMessage.includes('MBErrorDomain') && errorMessage.includes('211')) {
        // Find My iPhone is still enabled
        setError('FIND_MY_ENABLED');
      } else if (
        errorMessage.includes('already registered') ||
        errorMessage.includes('already in our system') ||
        errorMessage.includes('Server Error')
      ) {
        setError('DEVICE_ALREADY_REGISTERED');
        // Set custom data for support context before triggering bot
        Gleap.attachCustomData({
          install_step: "mdm_profile_install",
          error_code: "DEVICE_ALREADY_REGISTERED",
          device_serial: selectedDevice?.serial || "unknown",
          error_message: errorMessage,
        });
        Gleap.startBot('6980f8e0d2ef8797d6c8f0f9');
      } else {
        // Generic error - also trigger support bot
        setError(errorMessage);
        Gleap.attachCustomData({
          install_step: "mdm_profile_install",
          error_code: "UNKNOWN_ERROR",
          device_serial: selectedDevice?.serial || "unknown",
          error_message: errorMessage,
        });
        Gleap.startBot('6980f8e0d2ef8797d6c8f0f9');
      }
      
      setIsProcessing(false);
      setLoadingProgress(0);
    }
  };

  // Processing/Installing screen
  if (isProcessing) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Loader className="w-8 h-8 text-primary animate-spin" />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">Installing Filter</h2>
              <p className="text-muted-foreground">
                {selectedChild 
                  ? `Installing Shomri filter on ${selectedChild.firstName}'s device...`
                  : 'Installing Shomri filter on your device...'
                }
              </p>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full max-w-sm mx-auto">
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>Progress</span>
                <span>{loadingProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              This may take a few moments. Please do not close this window.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Find My iPhone still enabled - special friendly error screen
  if (error === 'FIND_MY_ENABLED') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md px-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">
                Find My iPhone is Still On
              </h2>
              <p className="text-muted-foreground">
                We can't install the filter while Find My iPhone is enabled. 
                Please turn it off and try again.
              </p>
            </div>
            
            <Button 
              onClick={() => navigate('/find-my-instructions')}
              className="w-full"
            >
              Turn Off Find My
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Device already registered - friendly error screen
  if (error === 'DEVICE_ALREADY_REGISTERED') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md px-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">
                Device Already Registered
              </h2>
              <p className="text-muted-foreground">
                It looks like this phone's serial number is already in our system. 
                Please reach out to customer support for further help.
              </p>
            </div>
            
            <Button 
              onClick={() => window.open('mailto:support@shomri.com', '_blank')}
              className="w-full"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }
  // Error screen
  if (error && !isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">
                {installedApps.length === 0 ? 'Unable to Load Apps' : 'Installation Failed'}
              </h2>
              <p className="text-muted-foreground mb-4">
                {error}
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setError(null);
                  if (installedApps.length === 0) {
                    setIsLoading(true);
                    // Re-trigger the useEffect
                    window.location.reload();
                  }
                }}
                className="w-full"
              >
                {installedApps.length === 0 ? 'Try Again' : 'Go Back'}
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate('/find-my-instructions')}
                className="w-full"
              >
                Back to Instructions
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading screen - show while fetching apps OR metadata
  if (isLoading || isLoadingMetadata) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-8 max-w-md px-4">
            {/* Animated nested squares loader */}
            <div className="flex justify-center">
              <NestedSquaresLoader 
                size="lg" 
                squareCount={10}
                speed={0.6}
              />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">Loading Apps</h2>
              <p className="text-muted-foreground">
                {isLoadingMetadata 
                  ? `Fetching app info (${metadataProgress.loaded}/${metadataProgress.total})...`
                  : 'Fetching installed apps from the device...'
                }
              </p>
            </div>

            {/* Progress bar for metadata loading */}
            {isLoadingMetadata && metadataProgress.total > 0 && (
              <div className="w-full max-w-sm mx-auto">
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-shomri-green via-shomri-purple to-shomri-blue h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(metadataProgress.loaded / metadataProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Dev environment fallback
  if (installedApps.length === 0 && !error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-6 max-w-md px-6">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-muted-foreground" />
            </div>
            
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">No Apps Found</h2>
              <p className="text-muted-foreground">
                App list unavailable - running in browser mode. In the native app, you'll see installed apps here.
              </p>
            </div>
            
            <Alert className="text-left">
              <AlertDescription className="text-sm">
                You can continue without selecting apps, or go back and try again with the device connected.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-3">
              <Button onClick={handleContinue} className="w-full">
                Continue Anyway
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/find-my-instructions')}
                className="w-full"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main app selection UI
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col p-6 space-y-4">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Choose Allowed Apps</h2>
          <p className="text-muted-foreground">
            These are all the apps currently on {selectedChild?.firstName || 'this'}'s phone. 
            Select which ones you want to <span className="font-medium text-foreground">allow</span>. 
            Unchecked apps will be <span className="font-medium text-foreground">blocked</span>.
          </p>
        </div>

        {/* Search and Quick Actions */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search apps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleSelectAll}
            className="w-full"
          >
            <Check className="w-4 h-4 mr-1" />
            Allow All
          </Button>
        </div>

        {/* App List */}
        <Card className="flex-1 overflow-hidden">
          <CardHeader className="py-3 border-b">
            <CardDescription>
              {filteredApps.length} app{filteredApps.length !== 1 ? 's' : ''} 
              {searchQuery && ` matching "${searchQuery}"`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-480px)]">
              <div className="divide-y">
                {/* Third-party apps (non-Apple) */}
                {thirdPartyApps.map((bundleId) => (
                  <AppRow 
                    key={bundleId} 
                    bundleId={bundleId} 
                    appInfo={getAppInfo(bundleId)} 
                    isSelected={selectedApps.has(bundleId)}
                    onToggle={handleToggleApp}
                  />
                ))}

                {/* Apple Apps Collapsible Section */}
                {appleApps.length > 0 && (
                  <Collapsible 
                    open={appleAppsOpen} 
                    onOpenChange={(open) => {
                      setAppleAppsOpen(open);
                      if (open) {
                        setTimeout(() => {
                          appleAppsSectionRef.current?.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                          });
                        }, 100);
                      }
                    }}
                  >
                    <div ref={appleAppsSectionRef}>
                      <CollapsibleTrigger className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors">
                        <span className="font-medium text-foreground">
                          Apple Apps ({appleApps.length})
                        </span>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${appleAppsOpen ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent>
                      <div className="divide-y">
                        {appleApps.map((bundleId) => (
                          <AppRow 
                            key={bundleId} 
                            bundleId={bundleId} 
                            appInfo={getAppInfo(bundleId)} 
                            isSelected={selectedApps.has(bundleId)}
                            onToggle={handleToggleApp}
                          />
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="space-y-3 pt-2">
          <div className="text-center text-sm text-muted-foreground">
            <span className="text-green-600 dark:text-green-400 font-medium">
              {appsWithIcons.filter(id => selectedApps.has(id)).length}
            </span> allowed, {' '}
            <span className="text-red-600 dark:text-red-400 font-medium">
              {appsWithIcons.filter(id => !selectedApps.has(id)).length}
            </span> blocked
          </div>
          
          <Button 
            onClick={handleContinue} 
            className="w-full"
            size="lg"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Extracted AppRow component for cleaner rendering
interface AppRowProps {
  bundleId: string;
  appInfo: { name: string; icon: string | null; developer: string | null };
  isSelected: boolean;
  onToggle: (bundleId: string) => void;
}

const AppRow: React.FC<AppRowProps> = ({ bundleId, appInfo, isSelected, onToggle }) => (
  <label
    className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${
      isSelected 
        ? 'bg-primary/5 hover:bg-primary/10' 
        : 'hover:bg-muted/50 opacity-60'
    }`}
  >
    <Checkbox
      checked={isSelected}
      onCheckedChange={() => onToggle(bundleId)}
    />
    
    {/* App Icon */}
    <div className="w-10 h-10 rounded-lg bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center">
      <img 
        src={appInfo.icon!} 
        alt={appInfo.name}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
    
    {/* App Info */}
    <div className="flex-1 min-w-0">
      <div className="font-medium text-foreground truncate">
        {appInfo.name}
      </div>
      {appInfo.developer && (
        <div className="text-xs text-muted-foreground truncate">
          {appInfo.developer}
        </div>
      )}
    </div>
    
    {/* Status Badge */}
    <div className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
      isSelected 
        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    }`}>
      {isSelected ? 'Allowed' : 'Blocked'}
    </div>
  </label>
);

export default AppSelection;
